import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";

import {
  productAnalytics,
  useCreateSoilSightProject,
  useDuplicateSoilSightProject,
  useGetFarms,
  useGetFieldsByFarmId,
  useListSoilSightProjects,
  useRenameSoilSightProject,
  useTranslation,
  useUpdateSoilSightProjectSettingsRefinements,
  useUserContext,
} from "utils";

import { MAX_NAME_LENGTH, MAX_PROJECTS } from "consts";

import { SoilSightProject } from "models";

import { Button, Flex, Form, Icon, Input, Popover, Spin, Tooltip, Typography } from "components";

import {
  createSoilSightProjectWithDefaults,
  getNameValidationError,
  getNextSoilSightProjectName,
  resolveSoilSightLocationRegion,
  showSoilSightSaveFailed,
} from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";

type Props = {
  klimFarmId: string;
  activeProjectId: string | null;
  onSwitchProject: (soilSightProjectId: string) => void;
  onProjectCreated: (soilSightProjectId: string) => void;
  /** Projects currently in the soft-delete undo window — hidden from UI but not yet purged. Owned by the parent. */
  pendingDeleteIds: Set<string>;
  /** Trigger soft-deletion of a project. Owned by the parent (useProjectDeleteWithUndo). */
  onDeleteProject: (soilSightProjectId: string) => void;
  /** Saves the open project's unsaved edits, so a copy of it includes them. */
  onBeforeDuplicateActiveProject?: () => Promise<unknown>;
};

export const ProjectManagement = ({
  klimFarmId,
  activeProjectId,
  onSwitchProject,
  onProjectCreated,
  pendingDeleteIds,
  onDeleteProject,
  onBeforeDuplicateActiveProject,
}: Props) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { user } = useUserContext();
  const { currentFarm } = useGetFarms();

  const listProjectsQuery = useListSoilSightProjects(klimFarmId);
  const projects = useMemo(() => listProjectsQuery.data ?? [], [listProjectsQuery.data]);

  const renameMutation = useRenameSoilSightProject(klimFarmId);
  const duplicateMutation = useDuplicateSoilSightProject(klimFarmId);
  const createMutation = useCreateSoilSightProject();
  const updateRefinements = useUpdateSoilSightProjectSettingsRefinements();
  const fieldsQuery = useGetFieldsByFarmId({ farmId: klimFarmId });

  const [isOpen, setIsOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<SoilSightProject | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialName, setCreateInitialName] = useState("");
  const [pendingDuplicateId, setPendingDuplicateId] = useState<string | null>(null);
  const [renameForm] = Form.useForm();
  const [createForm] = Form.useForm();

  // Track the active project ID locally so the label updates immediately on dropdown selection
  // without waiting for the context → header prop chain to complete.
  const [activeId, setActiveId] = useState<string | null>(activeProjectId);
  // Name fallback until the projects list refetch includes a newly created/duplicated project.
  const [pendingActiveName, setPendingActiveName] = useState<string | null>(null);
  // Preserved name of the last known active project — used when all projects are pending deletion
  // (undo window) so the header shows the deleted project name instead of "Unnamed project".
  const [lastActiveName, setLastActiveName] = useState<string | null>(null);
  // Immediate rename overlay — drives the list and title off local state so the UI updates without
  // waiting for RQ to propagate through the persist-client staleTime cycle.
  const [pendingRename, setPendingRename] = useState<{ id: string; name: string } | null>(null);

  useLayoutEffect(() => {
    setActiveId(activeProjectId);
  }, [activeProjectId]);

  const resolvedActiveId = activeId ?? activeProjectId;

  const displayProjects = useMemo(() => {
    const base = pendingRename ? projects.map((f) => (f.id === pendingRename.id ? { ...f, name: pendingRename.name } : f)) : projects;
    return base.filter((f) => !pendingDeleteIds.has(f.id));
  }, [projects, pendingRename, pendingDeleteIds]);

  const activeProject = displayProjects.find((f) => f.id === resolvedActiveId) ?? null;

  useLayoutEffect(() => {
    if (activeProject?.name) {
      setPendingActiveName(null);
      setLastActiveName(activeProject.name);
    }
  }, [activeProject?.name]);

  // Clear the overlay once the underlying RQ data has caught up.
  useEffect(() => {
    if (!pendingRename) return;
    const updated = projects.find((f) => f.id === pendingRename.id);
    if (updated?.name === pendingRename.name) setPendingRename(null);
  }, [projects, pendingRename]);

  const isLastProjectBeingDeleted = displayProjects.length === 0 && pendingDeleteIds.size > 0;
  const activeName = isLastProjectBeingDeleted
    ? t("projectManagement.deletingProject")
    : activeProject?.name ?? pendingActiveName ?? lastActiveName ?? t("projectManagement.unnamedProject");

  const existingProjectNames = useMemo(() => new Set(projects.map((f) => f.name?.trim().toLowerCase() ?? "")), [projects]);

  const renameExistingProjectNames = useMemo(
    () => new Set(projects.filter((f) => f.id !== renameTarget?.id).map((f) => f.name?.trim().toLowerCase() ?? "")),
    [projects, renameTarget?.id]
  );

  const isAtProjectLimit = projects.length >= MAX_PROJECTS;
  const isOnlyProject = displayProjects.length <= 1;

  const getProjectNameError = (value: string, existingNames: Set<string>): string | null => getNameValidationError(value, t, existingNames);

  const createNameRules = [
    {
      validator: async (_: unknown, value: string | undefined) => {
        const v = value ?? "";
        if (v === "") return Promise.reject(new Error(t("projectManagement.nameRequired")));
        const err = getProjectNameError(v, existingProjectNames);
        if (err) return Promise.reject(new Error(err));
        return Promise.resolve();
      },
    },
  ];

  const renameNameRules = [
    {
      validator: async (_: unknown, value: string | undefined) => {
        const v = value ?? "";
        if (v === "") return Promise.reject(new Error(t("projectManagement.nameRequired")));
        const err = getProjectNameError(v, renameExistingProjectNames);
        if (err) return Promise.reject(new Error(err));
        return Promise.resolve();
      },
    },
  ];

  const handleSelect = (projectId: string) => {
    if (projectId === (activeId ?? activeProjectId)) return;
    setActiveId(projectId);
    setPendingActiveName(null);
    setIsOpen(false);
    onSwitchProject(projectId);
  };

  const handleStartRename = (project: SoilSightProject) => {
    setIsOpen(false);
    setRenameTarget(project);
    renameForm.setFieldsValue({ name: project.name ?? "" });
  };

  const handleCloseRename = () => {
    setRenameTarget(null);
    renameForm.resetFields();
  };

  const handleRenameConfirm = async () => {
    let values: { name: string } | null = null;
    try {
      values = await renameForm.validateFields();
    } catch {
      return;
    }
    const newName = values!.name.trim();
    const targetId = renameTarget!.id;

    // Drive the list and title off local state immediately so the user sees the update
    // before the RQ refetch cycle completes and persists to localStorage.
    setPendingRename({ id: targetId, name: newName });
    handleCloseRename();

    try {
      await renameMutation.mutateAsync({ soilSightProjectId: targetId, name: newName });
    } catch (saveError) {
      setPendingRename(null);
      showSoilSightSaveFailed(t, saveError);
    }
  };

  const getNextProjectName = (): string => getNextSoilSightProjectName(projects, (n) => t("projectManagement.defaultName", { n }));

  const handleDuplicate = async (projectId: string) => {
    setIsOpen(false);
    setPendingDuplicateId(projectId);
    try {
      // The copy is made from what the server has, so the open project's unsaved edits go first (a failed save is toasted).
      if (projectId === (activeId ?? activeProjectId)) await onBeforeDuplicateActiveProject?.();
      const source = projects.find((f) => f.id === projectId);
      const baseCopyName = source?.name ? `${source.name} ${t("projectManagement.copySuffix")}` : getNextProjectName();
      const lowerNames = new Set(projects.map((f) => f.name?.trim().toLowerCase() ?? ""));
      const truncated = baseCopyName.slice(0, MAX_NAME_LENGTH);
      let candidate = truncated;
      let counter = 2;
      while (lowerNames.has(candidate.trim().toLowerCase())) {
        const suffix = ` ${counter}`;
        candidate = `${truncated.slice(0, MAX_NAME_LENGTH - suffix.length)}${suffix}`;
        counter += 1;
      }
      const copyName = candidate;
      const result = await duplicateMutation.mutateAsync({ soilSightProjectId: projectId, name: copyName });
      if (result?.soilSightProjectId) {
        productAnalytics.track("soilsights:project_create", { creation_mode: "duplicate", project_count: projects.length + 1 });
        setPendingActiveName(copyName || null);
        setActiveId(result.soilSightProjectId);
        onProjectCreated(result.soilSightProjectId);
      }
    } catch (saveError) {
      showSoilSightSaveFailed(t, saveError);
    } finally {
      setPendingDuplicateId(null);
    }
  };

  const handleDelete = (projectId: string) => {
    setIsOpen(false);
    onDeleteProject(projectId);
  };

  const handleOpenCreate = () => {
    setIsOpen(false);
    const nextName = getNextProjectName();
    setCreateInitialName(nextName);
    createForm.setFieldsValue({ name: nextName });
    setCreateOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
    createForm.resetFields();
  };

  const handleCreateConfirm = async () => {
    let values: { name: string } | null = null;
    try {
      values = await createForm.validateFields();
    } catch {
      return;
    }
    try {
      const soilSightProjectId = await createSoilSightProjectWithDefaults({
        body: {
          farmId: klimFarmId,
          locationRegion: resolveSoilSightLocationRegion({ farmCountry: currentFarm?.country, userCountry: user?.farm?.country }),
          name: values!.name.trim(),
          referenceName: t("scenarioBuilder.reference"),
        },
        fields: fieldsQuery.data ?? [],
        create: (b) => createMutation.mutateAsync(b),
        assignField: (args) => updateRefinements.mutateAsync({ ...args, klimFarmId }),
      });
      if (soilSightProjectId) {
        productAnalytics.track("soilsights:project_create", { creation_mode: "new", project_count: projects.length + 1 });
        setPendingActiveName(values!.name.trim());
        setActiveId(soilSightProjectId);
        onProjectCreated(soilSightProjectId);
      }
      handleCloseCreate();
    } catch (saveError) {
      showSoilSightSaveFailed(t, saveError);
    }
  };

  const ProjectList = (
    <Flex className="project-selection-menu navigation-list" vertical>
      <Flex className="project-selection-menu navigation-list container" vertical>
        {displayProjects.map((project) => (
          <Flex
            key={project.id}
            data-testid={`project-item-${project.id}`}
            className="project-selection-menu navigation-list item"
            vertical
            onClick={() => handleSelect(project.id)}
          >
            <Flex align="center" justify="space-between" gap="0.5rem">
              <Flex align="center" gap="0.5rem" style={{ flex: 1, minWidth: 0 }}>
                {resolvedActiveId === project.id ? <Icon iconName="tabler/circle-check" color="black" /> : <span style={{ minWidth: "1.5rem" }} />}
                <Typography key={`${project.id}-${project.name}`} variant="paragraph-m" className="project-name">
                  {project.name ?? t("projectManagement.unnamedProject")}
                </Typography>
              </Flex>
              <Flex align="center" gap="0.25rem" className="project-item-actions" onClick={(e) => e.stopPropagation()}>
                <Button type="text" className="project-action-button" onClick={() => handleStartRename(project)} dataTestId={`project-rename-${project.id}`}>
                  <Icon iconName="tabler/edit" size="s" />
                </Button>
                <Tooltip title={isAtProjectLimit ? t("projectManagement.projectLimitReached", { max: MAX_PROJECTS }) : undefined}>
                  <span>
                    <Button
                      type="text"
                      className="project-action-button"
                      onClick={() => handleDuplicate(project.id)}
                      loading={pendingDuplicateId === project.id}
                      disabled={isAtProjectLimit}
                      dataTestId={`project-duplicate-${project.id}`}
                    >
                      <Icon iconName="tabler/copy" size="s" />
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={isOnlyProject ? t("projectManagement.deleteLastProjectDisabled") : undefined}>
                  <span>
                    <Button
                      type="text"
                      className="project-action-button project-action-delete"
                      onClick={() => handleDelete(project.id)}
                      disabled={isOnlyProject}
                      dataTestId={`project-delete-${project.id}`}
                    >
                      <Icon iconName="tabler/trash" size="s" />
                    </Button>
                  </span>
                </Tooltip>
              </Flex>
            </Flex>
            <Typography key={`${project.id}-${project.scenarioCount}-${project.fieldId}`} variant="paragraph-s" color="grey-600" className="project-meta">
              {[
                (fieldsQuery.data ?? []).find((f) => f.id === project.fieldId)?.name ?? null,
                t("projectManagement.scenarioCount", { count: project.scenarioCount }),
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Flex>
        ))}
      </Flex>
      <Tooltip title={isAtProjectLimit ? t("projectManagement.projectLimitReached", { max: MAX_PROJECTS }) : undefined}>
        <Flex
          data-testid="project-new"
          className={`new-project-button${isAtProjectLimit ? " new-project-button--disabled" : ""}`}
          align="center"
          justify="flex-start"
          gap="1rem"
          onClick={isAtProjectLimit ? undefined : handleOpenCreate}
          style={isAtProjectLimit ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
        >
          <Icon iconName="klim/plus" color={isAtProjectLimit ? undefined : "green"} style={isAtProjectLimit ? { opacity: 0.4 } : undefined} />
          <Typography variant="paragraph-m" color={isAtProjectLimit ? "grey-600" : "green-400"}>
            {t("projectManagement.newProject")}
          </Typography>
        </Flex>
      </Tooltip>
    </Flex>
  );

  return (
    <>
      <Popover
        dataTestId="project-selection-menu-popover"
        className="project-selection-menu"
        placement="bottomLeft"
        arrow={false}
        content={ProjectList}
        trigger="click"
        open={isOpen && !isLastProjectBeingDeleted}
        onOpenChange={(open) => !isLastProjectBeingDeleted && setIsOpen(open)}
      >
        {listProjectsQuery.isPending ? (
          <Spin size="default" />
        ) : (
          <Flex
            data-testid="project-selection"
            className="project-selection-menu button pointer"
            justify="center"
            align="center"
            onClick={() => !isLastProjectBeingDeleted && setIsOpen(!isOpen)}
            gap="0.5rem"
          >
            <Typography key={`${activeId}-${activeName}`} variant="paragraph-m">
              {activeName}
            </Typography>
            <Icon className={`project-selection-menu collapse-chevron${!isOpen ? " reverse" : ""}`} iconName="tabler/chevron-up" />
          </Flex>
        )}
      </Popover>

      {renameTarget && (
        <div className="scenario-analysis-modal-overlay" onClick={handleCloseRename}>
          <div className="scenario-analysis-modal themed" onClick={(e) => e.stopPropagation()}>
            <div className="scenario-analysis-modal-header">
              <div className="scenario-analysis-modal-header-text">
                <Typography variant="h5" className="scenario-analysis-modal-title">
                  {t("projectManagement.renameProject")}
                </Typography>
              </div>
              <Button className="scenario-analysis-modal-close" onClick={handleCloseRename} aria-label={t("projectManagement.close")}>
                <Icon iconName="tabler/x" size="m" />
              </Button>
            </div>
            <Form form={renameForm} validateTrigger={["onChange", "onBlur"]} className="scenario-analysis-modal-form">
              <div className="scenario-analysis-modal-content">
                <Input
                  name="name"
                  dataTestId="project-name-input"
                  initialValue={renameTarget?.name ?? ""}
                  label={t("projectManagement.projectName")}
                  placeholder={t("projectManagement.projectNamePlaceholder")}
                  className="scenario-analysis-modal-title-input"
                  maxLength={MAX_NAME_LENGTH + 1}
                  rules={renameNameRules}
                  onPressEnter={handleRenameConfirm}
                />
              </div>
            </Form>
            <div className="scenario-analysis-modal-footer">
              <Flex justify="space-between" align="center" gap="1rem" style={{ width: "100%" }}>
                <Button type="text" onClick={handleCloseRename}>
                  {t("projectManagement.cancel")}
                </Button>
                <Button
                  type="primary"
                  className="button-no-min-width auto-width"
                  onClick={handleRenameConfirm}
                  loading={renameMutation.isPending}
                  style={{ maxWidth: "fit-content" }}
                  dataTestId="project-rename-save"
                >
                  {t("projectManagement.save")}
                </Button>
              </Flex>
            </div>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="scenario-analysis-modal-overlay" onClick={handleCloseCreate}>
          <div className="scenario-analysis-modal themed" onClick={(e) => e.stopPropagation()}>
            <div className="scenario-analysis-modal-header">
              <div className="scenario-analysis-modal-header-text">
                <Typography variant="h5" className="scenario-analysis-modal-title">
                  {t("projectManagement.createProject")}
                </Typography>
              </div>
              <Button className="scenario-analysis-modal-close" onClick={handleCloseCreate} aria-label={t("projectManagement.close")}>
                <Icon iconName="tabler/x" size="m" />
              </Button>
            </div>
            <Form form={createForm} validateTrigger={["onChange", "onBlur"]} className="scenario-analysis-modal-form">
              <div className="scenario-analysis-modal-content">
                <Input
                  name="name"
                  dataTestId="project-name-input"
                  initialValue={createInitialName}
                  label={t("projectManagement.projectName")}
                  placeholder={t("projectManagement.projectNamePlaceholder")}
                  className="scenario-analysis-modal-title-input"
                  maxLength={MAX_NAME_LENGTH + 1}
                  rules={createNameRules}
                  onPressEnter={handleCreateConfirm}
                />
              </div>
            </Form>
            <div className="scenario-analysis-modal-footer">
              <Flex justify="space-between" align="center" gap="1rem" style={{ width: "100%" }}>
                <Button type="text" onClick={handleCloseCreate}>
                  {t("projectManagement.cancel")}
                </Button>
                <Button
                  type="primary"
                  className="button-no-min-width auto-width"
                  onClick={handleCreateConfirm}
                  loading={createMutation.isPending}
                  style={{ maxWidth: "fit-content" }}
                  dataTestId="project-create-submit"
                >
                  {t("projectManagement.create")}
                </Button>
              </Flex>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
